import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calculator, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ManualInputSection = ({ onCalculate, loading }) => {
  const [formData, setFormData] = useState({
    product_name: "",
    product_type: "food",
    energy_kj: "",
    saturated_fat_g: "",
    free_sugars_g: "",
    salt_g: "",
    fruit_veg_nut_seeds_percent: "0",
    fibre_g: "",
    protein_g: "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    const requiredFields = [
      "energy_kj",
      "saturated_fat_g",
      "free_sugars_g",
      "salt_g",
      "fibre_g",
      "protein_g",
    ];

    for (const field of requiredFields) {
      if (!formData[field] || formData[field] === "") {
        toast.error(
          `Please fill in all required fields: ${field.replace(/_/g, " ")}`
        );
        return;
      }
    }

    // Convert to numbers
    const nutrients = {
      product_name: formData.product_name || "Manual Entry",
      product_type: formData.product_type,
      energy_kj: parseFloat(formData.energy_kj),
      saturated_fat_g: parseFloat(formData.saturated_fat_g),
      free_sugars_g: parseFloat(formData.free_sugars_g),
      salt_g: parseFloat(formData.salt_g),
      fruit_veg_nut_seeds_percent: parseFloat(formData.fruit_veg_nut_seeds_percent),
      fibre_g: parseFloat(formData.fibre_g),
      protein_g: parseFloat(formData.protein_g),
    };

    // Validate numbers
    for (const [key, value] of Object.entries(nutrients)) {
      if (typeof value === "number" && isNaN(value)) {
        toast.error(`Invalid number for ${key.replace(/_/g, " ")}`);
        return;
      }
    }

    onCalculate(nutrients);
  };

  return (
    <Card className="shadow-xl border-0 bg-white" data-testid="manual-input-card">
      <CardContent className="p-8">
        <h2 className="text-2xl font-bold text-[#3b444d] mb-4">
          Manual Nutritional Entry
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Using NPM 2018 model (effective January 2026 for HFSS advertising restrictions)
        </p>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Product Info */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="product_name" className="text-base font-semibold text-[#3b444d]">
                Product Name (Optional)
              </Label>
              <Input
                id="product_name"
                type="text"
                placeholder="e.g., My Custom Product"
                value={formData.product_name}
                onChange={(e) => handleInputChange("product_name", e.target.value)}
                className="mt-2 h-12"
                data-testid="input-product-name"
              />
            </div>

            <div>
              <Label className="text-base font-semibold text-[#3b444d] mb-3 block">
                Product Type *
              </Label>
              <RadioGroup
                value={formData.product_type}
                onValueChange={(value) => handleInputChange("product_type", value)}
                className="flex gap-6"
                data-testid="manual-product-type-selector"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="food" id="manual-food" data-testid="manual-product-type-food" />
                  <Label htmlFor="manual-food" className="cursor-pointer">
                    Food (threshold ≥ 4)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="drink" id="manual-drink" data-testid="manual-product-type-drink" />
                  <Label htmlFor="manual-drink" className="cursor-pointer">
                    Drink (threshold ≥ 1)
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          {/* A Points (Negative Nutrients) */}
          <div className="p-6 bg-red-50 rounded-xl">
            <h3 className="text-xl font-bold text-[#e83b84] mb-6">
              A Points - Negative Nutrients (per 100g)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="energy_kj" className="font-semibold">
                  Energy (kJ) *
                </Label>
                <Input
                  id="energy_kj"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="e.g., 1500"
                  value={formData.energy_kj}
                  onChange={(e) => handleInputChange("energy_kj", e.target.value)}
                  className="mt-2 h-12"
                  data-testid="input-energy"
                  required
                />
              </div>
              <div>
                <Label htmlFor="saturated_fat_g" className="font-semibold">
                  Saturated Fat (g) *
                </Label>
                <Input
                  id="saturated_fat_g"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="e.g., 5.2"
                  value={formData.saturated_fat_g}
                  onChange={(e) => handleInputChange("saturated_fat_g", e.target.value)}
                  className="mt-2 h-12"
                  data-testid="input-saturated-fat"
                  required
                />
              </div>
              <div>
                <Label htmlFor="free_sugars_g" className="font-semibold">
                  Free Sugars (g) * <span className="text-xs text-gray-600">(NPM 2018)</span>
                </Label>
                <Input
                  id="free_sugars_g"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="e.g., 10.5"
                  value={formData.free_sugars_g}
                  onChange={(e) => handleInputChange("free_sugars_g", e.target.value)}
                  className="mt-2 h-12"
                  data-testid="input-free-sugars"
                  required
                />
              </div>
              <div>
                <Label htmlFor="salt_g" className="font-semibold">
                  Salt (g) * <span className="text-xs text-gray-600">(NPM 2018)</span>
                </Label>
                <Input
                  id="salt_g"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g., 0.5"
                  value={formData.salt_g}
                  onChange={(e) => handleInputChange("salt_g", e.target.value)}
                  className="mt-2 h-12"
                  data-testid="input-salt"
                  required
                />
              </div>
            </div>
          </div>

          {/* C Points (Positive Nutrients) */}
          <div className="p-6 bg-green-50 rounded-xl">
            <h3 className="text-xl font-bold text-[#cbcb3c] mb-6">
              C Points - Positive Nutrients (per 100g)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="fruit_veg_nut_seeds_percent" className="font-semibold">
                  Fruit/Vegetable/Nut/Seeds Content (%) <span className="text-xs text-gray-600">(NPM 2018)</span>
                </Label>
                <Input
                  id="fruit_veg_nut_seeds_percent"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  placeholder="e.g., 40"
                  value={formData.fruit_veg_nut_seeds_percent}
                  onChange={(e) => handleInputChange("fruit_veg_nut_seeds_percent", e.target.value)}
                  className="mt-2 h-12"
                  data-testid="input-fruit-veg-nut-seeds"
                />
              </div>
              <div>
                <Label htmlFor="fibre_g" className="font-semibold">
                  Fibre - AOAC (g) *
                </Label>
                <Input
                  id="fibre_g"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="e.g., 2.5"
                  value={formData.fibre_g}
                  onChange={(e) => handleInputChange("fibre_g", e.target.value)}
                  className="mt-2 h-12"
                  data-testid="input-fibre"
                  required
                />
              </div>
              <div>
                <Label htmlFor="protein_g" className="font-semibold">
                  Protein (g) *
                </Label>
                <Input
                  id="protein_g"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="e.g., 3.8"
                  value={formData.protein_g}
                  onChange={(e) => handleInputChange("protein_g", e.target.value)}
                  className="mt-2 h-12"
                  data-testid="input-protein"
                  required
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-gradient-to-r from-[#e83b84] to-[#64c2c8] hover:opacity-90 text-white font-bold text-lg"
            data-testid="calculate-manual-button"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Calculator className="w-5 h-5 mr-2" />
            )}
            Calculate Nutrient Profile (NPM 2018)
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ManualInputSection;
